import { useMemo, useState } from "react";

import { formatDuration } from "../../format";
import {
  DEPTH_LABELS,
  LAYOUT_LABELS,
  MAX_PEOPLE,
  SIDE_LABELS,
  depthChoices,
  hallSizeChoices,
  hallsFor,
  layoutChoices,
  sideChoices,
  toggle,
  zonesLabel,
  type Criteria,
} from "../../search";
import type { Bounds, Movie } from "../../types";
import { fold } from "../CinemaPicker";
import { Poster } from "../FilmList";

type Patch = (patch: Partial<Criteria>) => void;

/* ---------- film : liste illustrée + recherche, jamais un select ---------- */

export function FilmStep({
  movies,
  criteria,
  update,
}: {
  movies: Movie[];
  criteria: Criteria;
  update: Patch;
}) {
  const [query, setQuery] = useState("");

  const matches = useMemo(() => {
    const needle = fold(query);
    if (!needle) return movies;
    return movies.filter((movie) => fold(`${movie.title} ${movie.genres.join(" ")}`).includes(needle));
  }, [movies, query]);

  return (
    <>
      <input
        className="search-field"
        type="search"
        placeholder="Chercher un film à l'affiche…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {matches.length === 0 && <p className="state">Aucun film ne correspond à « {query} ».</p>}

      <ul className="film-picker">
        {matches.map((movie) => (
          <li key={movie.slug}>
            <button
              type="button"
              className={movie.slug === criteria.movieSlug ? "film-choice is-on" : "film-choice"}
              onClick={() => update({ movieSlug: movie.slug })}
            >
              <Poster movie={movie} />
              <span style={{ minWidth: 0 }}>
                <span className="film-choice-title">{movie.title}</span>
                <span className="film-choice-meta">
                  {formatDuration(movie.duration)} · {movie.showtimes.length} séance
                  {movie.showtimes.length > 1 ? "s" : ""}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ---------- options en ligne : le plan au-dessus porte l'illustration ---------- */

function OptionRow<T extends string>({
  choices,
  value,
  onSelect,
}: {
  choices: { value: T; label: string; hint?: string; disabled?: boolean }[];
  value: T;
  onSelect: (next: T) => void;
}) {
  return (
    <div className="option-grid">
      {choices.map((choice) => (
        <button
          key={choice.value}
          type="button"
          className={choice.value === value ? "option-row is-on" : "option-row"}
          disabled={choice.disabled}
          onClick={() => onSelect(choice.value)}
        >
          <span>{choice.label}</span>
          {choice.hint && <span className="option-row-hint">{choice.hint}</span>}
        </button>
      ))}
    </div>
  );
}

/** Zones cumulables : on coche celles qui conviennent, tout coché = aucune contrainte. */
function ZoneToggles<T extends string>({
  choices,
  values,
  onToggle,
}: {
  choices: { value: T; label: string; hint?: string }[];
  values: T[];
  onToggle: (value: T) => void;
}) {
  return (
    <div className="zone-toggles">
      {choices.map((choice) => {
        const on = values.includes(choice.value);
        return (
          <button
            key={choice.value}
            type="button"
            className={on ? "zone-toggle is-on" : "zone-toggle"}
            aria-pressed={on}
            onClick={() => onToggle(choice.value)}
          >
            <span className="zone-check" aria-hidden="true">
              {on ? "✓" : ""}
            </span>
            <span>
              <span className="zone-toggle-label">{choice.label}</span>
              {choice.hint && <span className="zone-toggle-hint">{choice.hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function SizeStep({
  movie,
  bounds,
  criteria,
  update,
}: {
  movie: Movie;
  bounds: Bounds;
  criteria: Criteria;
  update: Patch;
}) {
  const choices = hallSizeChoices(movie, bounds).map((choice) => {
    const { count, halls } = hallsFor(movie, choice.value, bounds);
    return {
      ...choice,
      hint:
        count === 0
          ? `${choice.hint} · aucune séance`
          : `${choice.hint} · ${count} séance${count > 1 ? "s" : ""} · salle${halls.length > 1 ? "s" : ""} ${halls.slice(0, 3).join(", ")}`,
    };
  });

  return <OptionRow choices={choices} value={criteria.hallSize} onSelect={(hallSize) => update({ hallSize })} />;
}

export function DepthStep({
  movie,
  criteria,
  update,
}: {
  movie: Movie;
  criteria: Criteria;
  update: Patch;
}) {
  return (
    <ZoneToggles
      choices={depthChoices(movie)}
      values={criteria.depth}
      onToggle={(zone) => update({ depth: toggle(criteria.depth, zone) })}
    />
  );
}

export function SideStep({ criteria, update }: { criteria: Criteria; update: Patch }) {
  return (
    <ZoneToggles
      choices={sideChoices()}
      values={criteria.side}
      onToggle={(zone) => update({ side: toggle(criteria.side, zone) })}
    />
  );
}

/* ---------- combien et comment, en une seule étape ---------- */

export function GroupStep({ criteria, update }: { criteria: Criteria; update: Patch }) {
  return (
    <div className="group-step">
      <div className="stepper">
        <button
          type="button"
          onClick={() => update({ people: criteria.people - 1 })}
          disabled={criteria.people <= 1}
          aria-label="Une personne de moins"
        >
          −
        </button>
        <span className="stepper-value">
          {criteria.people} personne{criteria.people > 1 ? "s" : ""}
        </span>
        <button
          type="button"
          onClick={() => update({ people: criteria.people + 1 })}
          disabled={criteria.people >= MAX_PEOPLE}
          aria-label="Une personne de plus"
        >
          +
        </button>
      </div>

      <span className="field-label">Comment vous placer</span>
      <div className="layout-row">
        {layoutChoices(criteria.people).map((choice) => (
          <button
            key={choice.value}
            type="button"
            className={choice.value === criteria.layout ? "layout-card is-on" : "layout-card"}
            disabled={choice.disabled}
            onClick={() => update({ layout: choice.value })}
          >
            <span className="layout-label">{choice.label}</span>
            <span className="layout-hint">{choice.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- récapitulatif ---------- */

export function RecapStep({
  movie,
  criteria,
  bounds,
  compact,
  candidates,
}: {
  movie: Movie;
  criteria: Criteria;
  bounds: Bounds;
  compact: boolean;
  candidates: number;
}) {
  const size = hallSizeChoices(movie, bounds).find((choice) => choice.value === criteria.hallSize);

  const rows: [string, string][] = [
    ["Film", movie.title],
    ["Taille", size?.label ?? "Peu importe"],
    ["Où s'asseoir", zonesLabel(criteria.depth, DEPTH_LABELS, "Toute la salle")],
    ...(compact
      ? []
      : ([["Latéral", zonesLabel(criteria.side, SIDE_LABELS, "Toute la largeur")]] as [
          string,
          string,
        ][])),
    ["Groupe", `${criteria.people} personne${criteria.people > 1 ? "s" : ""}`],
    ...(criteria.people >= 2
      ? ([["Disposition", LAYOUT_LABELS[criteria.layout]]] as [string, string][])
      : []),
    ["À examiner", `${candidates} séance${candidates > 1 ? "s" : ""}`],
  ];

  return (
    <dl className="recap">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
